import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Skeleton,
  Stack,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC } from 'react';

import {
  PieceAnnotation,
  TrackingNumberAnnouncementStatus,
  TrackingNumberValidationStatus,
  Workflow,
} from '@apiContract';

import { endAdornmentContainerSx, sxChip, sxTrackingNumber } from '../styles';
import { getTooltip } from '../utils';

const error = {
  duplicate: 'Tracking number is associated with an existing active item',
  ignore: 'Tracking number is associated with an existing active item and will be ignored',
  badFormat: 'Tracking number has improper format',
  badFormatIgnore: 'Tracking number has improper format and will be ignored',
  sendFailed: 'Tracking number was unable to be sent to lockers',
  sendFailedIgnore: 'Tracking number was unable to be sent to locker and will be ignored',
};

const getError = (
  workflow: Workflow,
  item?: PieceAnnotation,
  isAnyValid?: boolean,
  hasRecipient?: boolean,
  allAnnouncementsFinished?: boolean,
) => {
  if (!item) return;

  const validationStatus = item.pieceAnnotationsTrackingNumber?.validationStatus;
  const announcementStatus = item.pieceAnnotationsTrackingNumber?.announcementStatus;
  const isLockers = workflow === Workflow.Lockers;

  if (!isLockers) {
    switch (validationStatus) {
      case TrackingNumberValidationStatus.Conflict:
        return {
          message: error.ignore,
          color: 'text.secondary',
        };
      case TrackingNumberValidationStatus.BadFormat:
        return {
          message: error.badFormatIgnore,
          color: 'text.secondary',
        };
      default:
        return null;
    }
  }

  if (!hasRecipient) return null;

  const shouldShowGrayIgnoreMessage = isAnyValid || !allAnnouncementsFinished;

  switch (validationStatus) {
    case TrackingNumberValidationStatus.Conflict: {
      if (!allAnnouncementsFinished) return null;

      return {
        message: shouldShowGrayIgnoreMessage ? error.ignore : error.duplicate,
        color: shouldShowGrayIgnoreMessage ? 'text.secondary' : 'error.main',
      };
    }

    case TrackingNumberValidationStatus.BadFormat: {
      if (!allAnnouncementsFinished) return null;

      return {
        message: shouldShowGrayIgnoreMessage ? error.badFormatIgnore : error.badFormat,
        color: shouldShowGrayIgnoreMessage ? 'text.secondary' : 'error.main',
      };
    }

    case TrackingNumberValidationStatus.Valid: {
      if (announcementStatus === TrackingNumberAnnouncementStatus.Failed) {
        return {
          message: shouldShowGrayIgnoreMessage ? error.sendFailedIgnore : error.sendFailed,
          color: shouldShowGrayIgnoreMessage ? 'text.secondary' : 'error.main',
        };
      }
      return null;
    }

    default:
      return null;
  }
};

const Empty = (
  <InputAdornment position="start">
    <Skeleton sx={{ width: 117, height: 14, borderRadius: 0.5 }} />
  </InputAdornment>
);

const Circle = (
  <Box display="flex" justifyContent="center">
    <CircularProgress
      size={22}
      sx={(theme: Theme) => ({
        color: theme.palette.grey[500],
      })}
    />
  </Box>
);

type Props = {
  item?: PieceAnnotation;
  workflow: Workflow;
  isAnyValid?: boolean;
  hasRecipient: boolean;
  allAnnouncementsFinished?: boolean;
};

const TrackInput: FC<Props> = ({ isAnyValid, item, workflow, hasRecipient, allAnnouncementsFinished }) => {
  const startAdornment = item && item.textValue ? null : Empty;
  const announcementStatus = item?.pieceAnnotationsTrackingNumber?.announcementStatus;
  const validationStatus = item?.pieceAnnotationsTrackingNumber?.validationStatus;

  const shouldShowSpinner =
    workflow === Workflow.Lockers &&
    hasRecipient &&
    validationStatus === TrackingNumberValidationStatus.Valid &&
    (announcementStatus === TrackingNumberAnnouncementStatus.None ||
      announcementStatus === TrackingNumberAnnouncementStatus.Sent);
  const endAdornment = shouldShowSpinner ? Circle : null;
  const tooltip = getTooltip(workflow, item, hasRecipient) || null;
  const errorText = getError(workflow, item, isAnyValid, hasRecipient, allAnnouncementsFinished);
  const carrierName = item?.pieceAnnotationsTrackingNumber?.shippingCarrier?.name;

  return (
    <Tooltip title={tooltip}>
      <Box sx={sxTrackingNumber}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="column">
            <Stack direction="row">
              {startAdornment}
              <Typography variant="body2">{item?.textValue || ''}</Typography>
            </Stack>
            {!!errorText && (
              <Typography color={errorText.color} variant="caption">
                {errorText.message}
              </Typography>
            )}
          </Stack>
          <Box sx={endAdornmentContainerSx}>
            {endAdornment}

            {carrierName && (
              <Stack direction="column" sx={{ ml: 2 }}>
                <Chip label={carrierName} size="small" variant="soft" color="default" sx={sxChip} />
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>
    </Tooltip>
  );
};

export default TrackInput;
